import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import type {
  Field,
  LayoutRelatedObjects,
  ObjectPermissions,
  ObjectTypeToLookupReferences,
} from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import { getFieldValueLabel, type FieldValueLabeler } from "./relatedObjectHelper";
import RelatedObjectsGrid, { type RelatedColumnLayout, type RelatedObjectCellDecorations } from "./RelatedObjectsGrid";
import type { RelatedObjectRowModel, RelatedObjectValuesState } from "./relatedObjectValuesState";
import { addRowAction, deleteRowAction, updateRowAction } from "./relatedObjectValuesState";
import type { ValidationErrorsByRow } from "./relatedObjectsValidation";

interface Props {
  layout: LayoutRelatedObjects;
  fields: Field[];
  objectPermissions: ObjectPermissions;
  rows: RelatedObjectRowModel[];
  setState: React.Dispatch<React.SetStateAction<RelatedObjectValuesState>>;
  allowEditing: boolean;
  allowAddingAndDeletingRows: boolean;
  loading?: boolean;
  lookupOptions: ObjectTypeToLookupReferences;
  onLookupOptionsNeeded: (lookupObjectTypes: string[]) => void;
  validationErrors: ValidationErrorsByRow;
  cellDecorations?: RelatedObjectCellDecorations;
  columnLayouts?: Record<string, RelatedColumnLayout>;
  noOptionsTexts?: Record<string, string>;
  onUserRowChange?: (
    rowId: string,
    oldRow: RelatedObjectRowModel,
    updatedRow: RelatedObjectRowModel,
    labelOf: FieldValueLabeler
  ) => void;
}

const RelatedObjectsGroup = ({
  layout,
  fields,
  objectPermissions,
  rows,
  setState,
  allowEditing,
  allowAddingAndDeletingRows,
  loading,
  lookupOptions,
  onLookupOptionsNeeded,
  validationErrors,
  cellDecorations,
  columnLayouts,
  noOptionsTexts,
  onUserRowChange,
  children,
}: PropsWithChildren<Props>) => {
  const { hasPermissions } = useClientContext();
  const [renderKey, toggleRenderKey] = useToggleState(false);

  const handleRowUpdate = useCallback(
    (updatedRow: RelatedObjectRowModel, oldRow: RelatedObjectRowModel) => {
      onUserRowChange?.(updatedRow.id, oldRow, updatedRow, (fieldId, value) =>
        getFieldValueLabel(fields, fieldId, value, lookupOptions, [
          ...(oldRow.rowState.lookupObjects[fieldId] ?? []),
          ...(updatedRow.rowState.lookupObjects[fieldId] ?? []),
        ])
      );
      setState(updateRowAction(layout.objectType, updatedRow));
    },
    [layout.objectType, setState, onUserRowChange, fields, lookupOptions]
  );

  const handleRowDelete = useCallback(
    (deletedRow: RelatedObjectRowModel) => {
      setState(deleteRowAction(layout.objectType, deletedRow.id));
      setTimeout(() => toggleRenderKey());
    },
    [layout.objectType, setState, toggleRenderKey]
  );

  const handleRowAdd = useCallback(() => {
    setState(addRowAction(layout.objectType));
    setTimeout(() => toggleRenderKey());
  }, [layout.objectType, setState, toggleRenderKey]);

  const canEdit = allowEditing && hasPermissions(objectPermissions.objectDataWritePermissions);
  const canAddAndDeleteRows = canEdit && allowAddingAndDeletingRows;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{layout.name}</Typography>
      <Stack spacing={1} sx={{ pb: 1, alignItems: "flex-start" }}>
        {rows.length === 0 && (
          <Box sx={{ pl: 2, pb: 2 }}>
            <Typography color="textSecondary">No records</Typography>
          </Box>
        )}

        {/* Without inline-size containment the columns' total width widens the whole card */}
        {rows.length > 0 && (
          <Paper variant="outlined" sx={{ width: "100%", py: 0.25, contain: "inline-size" }}>
            <RelatedObjectsGrid
              key={renderKey.toString()}
              rows={rows}
              fields={fields}
              lookupOptions={lookupOptions}
              sorting={layout.sorting}
              allowEditing={canEdit}
              allowDeletingRows={canAddAndDeleteRows}
              onRowUpdate={handleRowUpdate}
              onRowDelete={handleRowDelete}
              loading={loading}
              validationErrors={validationErrors}
              cellDecorations={cellDecorations}
              columnLayouts={columnLayouts}
              noOptionsTexts={noOptionsTexts}
              onLookupOptionsNeeded={onLookupOptionsNeeded}
            />
          </Paper>
        )}

        {(canAddAndDeleteRows || children) && (
          <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
            {canAddAndDeleteRows && (
              <Button variant="text" startIcon={<AddIcon />} onClick={handleRowAdd} disabled={loading}>
                Add New
              </Button>
            )}
            <Box sx={{ ml: "auto", pr: canAddAndDeleteRows ? "62px" : "11px" }}>{children}</Box>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default RelatedObjectsGroup;
